import { useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import type { AudioNote } from '@highlight/common'
import { useApi } from '@renderer/chat/api'
import { useGenerateAudioShareLink } from '@renderer/hooks/share-link'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'
import {
  buildSlackShareRequest,
  describeSlackShareError,
  sharedConversationIdFromLink,
  slackShareResponseSchema,
  type SlackDestination,
  type SlackShareResult,
} from './slack-share'

const log = GlobalLogger.bind('ShareToSlack')

/**
 * The gateway only posts content that is already public, so each kind carries what it takes to
 * publish it first. A meeting publishes through its audio note. A chat publishes through the share
 * control's link, which reuses a current link and refreshes an outdated one.
 */
export type ShareToSlackTarget =
  | { kind: 'meeting'; audioNote: AudioNote }
  | { kind: 'chat'; ensureShareLink: () => Promise<string> }

interface ShareToSlackInput {
  target: ShareToSlackTarget
  destination: SlackDestination
  message: string
}

/** Publishes the target if it isn't yet and returns the id the gateway's share route expects. */
function usePublishShareTarget() {
  const { mutateAsync: generateAudioShareLink } = useGenerateAudioShareLink()

  return useCallback(
    async (target: ShareToSlackTarget): Promise<string> => {
      if (target.kind === 'meeting') {
        if (!target.audioNote.shareLink) {
          const link = await generateAudioShareLink({ audioNote: target.audioNote, copyToClipboard: false })
          if (!link) {
            throw new Error('Meeting share link was not created')
          }
        }
        return target.audioNote.id
      }

      const link = await target.ensureShareLink()
      const sharedConversationId = sharedConversationIdFromLink(link)
      if (!sharedConversationId) {
        throw new Error(`Unrecognized chat share link: ${link}`)
      }
      return sharedConversationId
    },
    [generateAudioShareLink],
  )
}

export function useShareToSlack() {
  const { post } = useApi()
  const publishShareTarget = usePublishShareTarget()

  return useMutation({
    mutationKey: ['share-to-slack'],
    mutationFn: async ({ target, destination, message }: ShareToSlackInput): Promise<SlackShareResult> => {
      let id: string
      try {
        id = await publishShareTarget(target)
      } catch (error) {
        log.error(`publishing the ${target.kind} share failed: ${String(error)}`)
        throw new Error("Couldn't create a share link. Try again.")
      }

      const response = await post(
        'slack/share',
        JSON.stringify(buildSlackShareRequest({ kind: target.kind, id, destination, message })),
        { version: 'v1', headers: { 'Content-Type': 'application/json' } },
      )
      const body: unknown = await response.json().catch(() => null)
      if (!response.ok) {
        log.warn(`slack share failed status=${response.status}`)
        throw new Error(describeSlackShareError(response.status, body))
      }
      return slackShareResponseSchema.parse(body).data
    },
  })
}
